import * as mammoth from 'mammoth';
import * as XLSX from 'xlsx';
import TurndownService from 'turndown';
import { gfm } from 'turndown-plugin-gfm';
import * as pdfjsLib from 'pdfjs-dist';
import type { TextItem } from 'pdfjs-dist/types/src/display/api';
import { FileType } from '../types';

// Ensure the PDF.js worker is configured even if this module is used first.
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdfjs/pdf.worker.js`;

/**
 * Shared Turndown instance configured for GitHub-Flavored Markdown
 * (tables, strikethrough, task lists) with sensible heading/list styles.
 */
const createTurndown = (): TurndownService => {
  const service = new TurndownService({
    headingStyle: 'atx',
    bulletListMarker: '-',
    codeBlockStyle: 'fenced',
    emDelimiter: '*',
  });
  service.use(gfm);
  return service;
};

/**
 * Convert a DOCX file to Markdown, preserving headings, bold/italic,
 * lists, tables and links via mammoth's HTML conversion + Turndown.
 */
export const docxToMarkdown = async (
  file: File,
  onProgress?: (progress: number) => void
): Promise<string> => {
  try {
    if (onProgress) onProgress(10);
    const arrayBuffer = await file.arrayBuffer();
    if (onProgress) onProgress(30);

    const result = await mammoth.convertToHtml({ arrayBuffer });
    if (result.messages && result.messages.length > 0) {
      console.warn('Mammoth.js messages:', result.messages);
    }
    if (onProgress) onProgress(70);

    const markdown = createTurndown().turndown(result.value || '');
    if (onProgress) onProgress(100);

    return markdown.trim();
  } catch (error) {
    console.error('Error converting DOCX to Markdown:', error);
    throw new Error(
      `Failed to convert Word document to Markdown: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
};

/**
 * Convert an XLSX workbook to Markdown. Each worksheet becomes a
 * level-2 heading followed by a GFM table of its rows.
 */
export const xlsxToMarkdown = async (
  file: File,
  onProgress?: (progress: number) => void
): Promise<string> => {
  try {
    if (onProgress) onProgress(10);
    const arrayBuffer = await file.arrayBuffer();
    if (onProgress) onProgress(30);

    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    const sections: string[] = [];

    workbook.SheetNames.forEach((sheetName) => {
      const sheet = workbook.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
        header: 1,
        blankrows: false,
        defval: '',
      });

      sections.push(`## ${sheetName}`);

      if (rows.length === 0) {
        sections.push('_Empty sheet_');
        return;
      }

      sections.push(rowsToMarkdownTable(rows));
    });

    if (onProgress) onProgress(100);
    return sections.join('\n\n').trim();
  } catch (error) {
    console.error('Error converting XLSX to Markdown:', error);
    throw new Error(
      `Failed to convert Excel file to Markdown: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
};

/** Render an array of rows as a GitHub-Flavored Markdown table. */
const rowsToMarkdownTable = (rows: unknown[][]): string => {
  const colCount = rows.reduce((max, row) => Math.max(max, row.length), 0);
  if (colCount === 0) return '_Empty sheet_';

  const escapeCell = (value: unknown): string =>
    String(value ?? '')
      .replace(/\|/g, '\\|')
      .replace(/\r?\n/g, ' ')
      .trim();

  const normalize = (row: unknown[]): string[] => {
    const cells = row.map(escapeCell);
    while (cells.length < colCount) cells.push('');
    return cells;
  };

  const [headerRow, ...bodyRows] = rows;
  const header = normalize(headerRow);
  const lines = [
    `| ${header.join(' | ')} |`,
    `| ${header.map(() => '---').join(' | ')} |`,
    ...bodyRows.map((row) => `| ${normalize(row).join(' | ')} |`),
  ];
  return lines.join('\n');
};

interface PdfLine {
  text: string;
  x: number;
  y: number;
  fontSize: number;
}

/**
 * Convert a PDF to Markdown using positional/font-size heuristics.
 *
 * This is best-effort: PDFs carry no semantic structure, so headings are
 * inferred from relative font size, list items from leading glyphs, and
 * paragraph breaks from vertical gaps. It is an approximation, not a
 * faithful reproduction of the original layout.
 */
export const pdfToMarkdown = async (
  file: File,
  onProgress?: (progress: number) => void
): Promise<string> => {
  try {
    if (onProgress) onProgress(5);
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

    const pageMarkdowns: string[] = [];

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();

      const lines = groupItemsIntoLines(
        textContent.items.filter((item): item is TextItem => 'str' in item)
      );

      pageMarkdowns.push(linesToMarkdown(lines));

      if (onProgress) {
        onProgress(5 + Math.round((pageNum / pdf.numPages) * 90));
      }
    }

    const markdown = pageMarkdowns
      .map((m) => m.trim())
      .filter(Boolean)
      .join('\n\n---\n\n');

    if (!markdown.trim()) {
      throw new Error('No text could be extracted. The PDF may be scanned or image-based.');
    }

    if (onProgress) onProgress(100);
    return markdown;
  } catch (error) {
    console.error('Error converting PDF to Markdown:', error);
    if (error instanceof Error) {
      if (error.message.includes('Invalid PDF')) {
        throw new Error('The file appears to be corrupted or is not a valid PDF.');
      }
      if (error.message.includes('password')) {
        throw new Error('This PDF is password-protected. Please remove the password and try again.');
      }
      throw error;
    }
    throw new Error('Failed to convert PDF to Markdown. Please try a different file.');
  }
};

/** Group raw PDF text items into ordered lines by vertical position. */
const groupItemsIntoLines = (items: TextItem[]): PdfLine[] => {
  const rawLines: { items: TextItem[]; y: number }[] = [];
  const Y_TOLERANCE = 3; // points; items within this share a line

  items.forEach((item) => {
    if (!item.str.trim() && item.str !== ' ') return;
    const y = item.transform[5];
    const existing = rawLines.find((line) => Math.abs(line.y - y) <= Y_TOLERANCE);
    if (existing) {
      existing.items.push(item);
    } else {
      rawLines.push({ items: [item], y });
    }
  });

  // PDF y-origin is bottom-left, so larger y is higher on the page.
  rawLines.sort((a, b) => b.y - a.y);

  return rawLines
    .map((line) => {
      const ordered = [...line.items].sort((a, b) => a.transform[4] - b.transform[4]);
      const text = ordered
        .map((i) => i.str)
        .join('')
        .replace(/\s+/g, ' ')
        .trim();
      const fontSize = Math.max(...ordered.map((i) => i.height || Math.abs(i.transform[3]) || 0));
      const x = Math.min(...ordered.map((i) => i.transform[4]));
      return { text, x, y: line.y, fontSize };
    })
    .filter((line) => line.text.length > 0);
};

/** Turn ordered lines into Markdown using font-size & glyph heuristics. */
const linesToMarkdown = (lines: PdfLine[]): string => {
  if (lines.length === 0) return '';

  // Body font size = most common font size across lines.
  const bodyFontSize = mostCommonFontSize(lines);

  const out: string[] = [];
  let prevY: number | null = null;

  lines.forEach((line) => {
    // Insert a blank line when the vertical gap suggests a new paragraph.
    if (prevY !== null) {
      const gap = prevY - line.y;
      if (gap > line.fontSize * 1.6) out.push('');
    }
    prevY = line.y;

    const bullet = line.text.match(/^([•◦▪‣·*-]|•)\s+(.*)$/);
    const numbered = line.text.match(/^(\d+)[.)]\s+(.*)$/);
    const ratio = line.fontSize / bodyFontSize;

    if (bullet) {
      out.push(`- ${bullet[2]}`);
    } else if (numbered) {
      out.push(`${numbered[1]}. ${numbered[2]}`);
    } else if (ratio >= 1.6) {
      out.push(`# ${line.text}`);
    } else if (ratio >= 1.3) {
      out.push(`## ${line.text}`);
    } else if (ratio >= 1.15) {
      out.push(`### ${line.text}`);
    } else {
      out.push(line.text);
    }
  });

  // Collapse 3+ blank lines and join.
  return out.join('\n').replace(/\n{3,}/g, '\n\n');
};

const mostCommonFontSize = (lines: PdfLine[]): number => {
  const counts = new Map<number, number>();
  lines.forEach((line) => {
    const size = Math.round(line.fontSize);
    counts.set(size, (counts.get(size) || 0) + 1);
  });
  let best = 0;
  let bestCount = -1;
  counts.forEach((count, size) => {
    if (count > bestCount) {
      bestCount = count;
      best = size;
    }
  });
  return best || 12;
};

/** Dispatch Markdown conversion based on file type. */
export const convertToMarkdown = async (
  file: File,
  fileType: FileType,
  onProgress?: (progress: number) => void
): Promise<string> => {
  switch (fileType) {
    case 'pdf':
      return pdfToMarkdown(file, onProgress);
    case 'docx':
      return docxToMarkdown(file, onProgress);
    case 'xlsx':
      return xlsxToMarkdown(file, onProgress);
    default:
      throw new Error(`Unsupported file type for Markdown conversion: ${fileType}`);
  }
};
